import React from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { LogoWithDomainSwitcher } from '@/shared/components/layout/LogoWithDomainSwitcher';
import {
  ChevronLeft,
  ChevronRight,
  X,
  Building2,
  Users,
  Briefcase,
  Newspaper,
  Sparkles
} from 'lucide-react';

interface SidebarProps {
  isOpen: boolean;
  onClose: () => void;
  isCollapsed: boolean;
  onToggleCollapse: () => void;
}

interface NavigationItem {
  name: string;
  href: string;
  icon: React.ForwardRefExoticComponent<any>;
  shortName?: string;
}

interface NavigationGroup {
  label: string;
  items: NavigationItem[];
}

/**
 * CRMSidebar component - Navigation sidebar for CRM domain
 * Responsive design with mobile support
 */
const Sidebar: React.FC<SidebarProps> = ({ isOpen, onClose, isCollapsed, onToggleCollapse }) => {
  const location = useLocation();
  
  // Navigation groups for CRM domain
  const getNavigationGroups = (): NavigationGroup[] => {
    return [
      {
        label: 'Clients',
        items: [
          {
            name: 'Companies',
            href: '/crm/companies',
            icon: Building2,
            shortName: 'Companies'
          },
          {
            name: 'Key Moments',
            href: '/crm/moments',
            icon: Sparkles,
            shortName: 'Key Moments'
          },
          {
            name: 'Job Posts',
            href: '/crm/job-posts',
            icon: Briefcase,
            shortName: 'Job Posts'
          },
          {
            name: 'News',
            href: '/crm/news',
            icon: Newspaper,
            shortName: 'News'
          },
        ]
      },
      {
        label: 'POCs',
        items: [
          {
            name: 'People',
            href: '/crm/people',
            icon: Users,
            shortName: 'People'
          },
          {
            name: 'Role Changes',
            href: '/crm/role-changes',
            icon: Users,
            shortName: 'Role Changes'
          },
        ]
      }
    ];
  };

  const navigationGroups = getNavigationGroups();

  const isActiveRoute = (href: string) => {
    // Exact match
    if (location.pathname === href) return true;
    
    // Check if path starts with href + '/'
    if (location.pathname.startsWith(href + '/')) {
      // Exclude company detail tab routes (e.g., /crm/news/companies/465)
      // These should not highlight the main sidebar items
      if (location.pathname.includes('/companies/')) {
        return false;
      }
      return true;
    }
    
    return false;
  };

  return (
    <>
      {/* Mobile overlay */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-gray-600 bg-opacity-50 z-40 lg:hidden"
          onClick={onClose}
        />
      )}

      {/* Sidebar - Floating Container */}
      <div
        className={`fixed z-50 bg-white transform transition-all duration-300 ease-in-out
          lg:left-4 lg:top-4 lg:bottom-4 lg:rounded-2xl lg:shadow-2xl lg:translate-x-0
          inset-y-0 left-0 shadow-lg
          ${isOpen ? 'translate-x-0' : '-translate-x-full'}
          ${isCollapsed ? 'w-20' : 'w-72'}
        `}
      >
        <div className="flex flex-col h-full lg:rounded-2xl overflow-hidden">
          {/* Sidebar header - Mobile close button only */}
          <div className="flex items-center justify-end p-4 border-b border-gray-200 lg:hidden">
            <button
              onClick={onClose}
              className="p-1 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Collapse toggle button - Desktop only, positioned outside overflow container */}
          <button
            onClick={onToggleCollapse}
            className="hidden lg:flex absolute -right-3 top-[68px] z-[60] w-6 h-6 items-center justify-center bg-white rounded-full border border-gray-200 shadow-md hover:shadow-lg hover:border-gray-300 hover:scale-110 transition-all cursor-pointer"
            title={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            type="button"
          >
            {isCollapsed ? (
              <ChevronRight className="h-3.5 w-3.5 text-gray-600" />
            ) : (
              <ChevronLeft className="h-3.5 w-3.5 text-gray-600" />
            )}
          </button>

          {/* Logo with integrated Domain Switcher */}
          <LogoWithDomainSwitcher isCollapsed={isCollapsed} />

          {/* Navigation */}
          <nav className="flex-1 px-4 py-5 overflow-y-auto">
            {navigationGroups.map((group, groupIndex) => (
              <div key={group.label} className={groupIndex > 0 ? 'mt-6' : ''}>
                {/* Section Header */}
                {!isCollapsed && (
                  <div className="px-3 mb-2">
                    <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider">
                      {group.label}
                    </h3>
                  </div>
                )}
                
                {/* Section Items */}
                <div className="space-y-1">
                  {group.items.map((item) => {
                    const Icon = item.icon;
                    const isActive = isActiveRoute(item.href);
                    
                    return (
                      <div key={item.name}>
                        <NavLink
                          to={item.href}
                          onClick={() => {
                            if (window.innerWidth < 1024) {
                              onClose();
                            }
                          }}
                          className={`group flex items-center text-sm font-medium rounded-lg transition-all ${
                            isCollapsed ? 'px-3 py-3 justify-center' : 'px-3 py-2'
                          } ${
                            isActive
                              ? 'bg-gradient-to-r from-[#5b3d72] to-[#704a8b] text-white shadow-md'
                              : 'text-gray-600 hover:bg-[#ebe4f1]/50 hover:text-[#311c3b]'
                          }`}
                          title={item.name}
                        >
                          <Icon
                            className={`h-5 w-5 transition-colors ${
                              isCollapsed ? '' : 'mr-3'
                            } ${
                              isActive ? 'text-white' : 'text-gray-400 group-hover:text-[#311c3b]'
                            }`}
                          />
                          {!isCollapsed && (
                            <span className="flex-1">
                              {item.shortName || item.name}
                            </span>
                          )}
                        </NavLink>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </nav>

        </div>
      </div>
    </>
  );
};

export default Sidebar;